import jsPDF from 'jspdf';
import type { Baustellenordnung } from '../../components/handbuch/types';
import type { NotrufplanInhalt } from '../../components/sige-plan/types';
import { sichereTextausgabe, winAnsiSicher } from './winAnsi';

/**
 * Der Aushang — die Bautafel am Bauzaun.
 *
 * Ein abgeleitetes Kurzdokument: kein eigener Datensatz, kein eigenes Modul,
 * keine Kachel, keine Migration. Er liest, was anderswo schon gepflegt ist,
 * und stellt es so gross dar, dass man es im Vorbeigehen liest.
 *
 * ═══ WARUM A3 HOCH UND NICHT A4 ═══════════════════════════════════════════
 * Entscheidung Nr. 4. Der Zweck des Blattes ist Lesbarkeit aus etwa drei
 * Metern — vom Tor aus, ohne stehenzubleiben. Auf A4 waeren die Notrufnummern
 * in der Groesse, die dafuer noetig ist, breiter als die Seite. A2 ist die
 * Option fuer grosse Baustellen; das Blatt ist dann exakt dasselbe, nur um
 * den Faktor 420/297 groesser gesetzt (siehe `SKALA`).
 *
 * ═══ WARUM KEIN html2canvas ═══════════════════════════════════════════════
 * Die Vorlage (`generators/OnePagerPage.tsx` der Quell-App) druckt HTML ueber
 * `window.print()`; der naheliegende Weg waere gewesen, denselben Block mit
 * html2canvas ins PDF zu rastern. Genau bei diesem Format ist das der
 * schlechteste Weg: html2canvas erzeugt eine Bitmap in Bildschirmaufloesung,
 * und was auf A4 gerade noch durchgeht, ist auf A2 vierfach vergroessert und
 * an jeder Kante ausgefranst. Ein Aushang, dessen Rufnummern verwaschen sind,
 * verfehlt seinen einzigen Zweck. Hier wird deshalb mit jsPDF gesetzt —
 * Vektorschrift, in jeder Groesse scharf.
 *
 * ═══ WARUM KEIN BRIEFKOPF ═════════════════════════════════════════════════
 * `letterhead.ts` baut Geschaeftsbriefe: Anschriftfeld, Falzmarken, Fusszeile
 * mit Bankverbindung, A4-Geometrie. Ein Aushang ist kein Geschaeftsbrief. Er
 * traegt keinen Absender im Briefkopfsinn und keine Unterschrift.
 *
 * ═══ WAS DIESES BLATT NICHT TUT ═══════════════════════════════════════════
 *   * Es erfasst KEINE Notfalldaten. Krankenhaus und Durchgangsarzt kommen aus
 *     dem Notrufplan des SiGe-Plans (`sige_bloecke` typ='notrufplan'). Fehlt
 *     der Block, steht an dieser Stelle ein ausdruecklicher Hinweis — niemals
 *     eine Luecke und erst recht keine erfundene Adresse. Sonst gaebe es zwei
 *     Krankenhaeuser im selben Projekt.
 *   * Es traegt KEINE Unterschriftenzeile. Der Aushang ist kein
 *     Einweisungsprotokoll (Entscheidung Nr. 6) und loest keine LV-Buchung
 *     aus; er darf beliebig oft gedruckt werden.
 */

// ═══════════════════════════════════════════════════════════════════════════
// FORMAT
// ═══════════════════════════════════════════════════════════════════════════

export type AushangFormat = 'a3' | 'a2';

/** Blattmasse in Millimetern, hoch. Kein A4 — Begruendung im Dateikopf. */
export const AUSHANG_MASSE: Record<AushangFormat, [number, number]> = {
    a3: [297, 420],
    a2: [420, 594],
};

/**
 * Vergroesserungsfaktor gegenueber A3.
 *
 * Das ganze Blatt wird in A3-Millimetern gesetzt und beim Zeichnen mit diesem
 * Faktor multipliziert — Koordinaten, Strichstaerken UND Schriftgroessen. Das
 * ist der Grund, warum es nur EINE Layoutbeschreibung gibt: A2 ist kein
 * zweites Blatt, sondern dasselbe Blatt in gross. Ein separat gepflegtes
 * A2-Layout waere nach der ersten Textaenderung ein anderes Dokument.
 */
const SKALA: Record<AushangFormat, number> = {
    a3: 1,
    a2: 420 / 297,
};

// ═══════════════════════════════════════════════════════════════════════════
// SCHRIFTGROESSEN — und warum genau diese
// ═══════════════════════════════════════════════════════════════════════════
//
// Faustregel der Beschilderung: 1 mm Versalhoehe je 300 mm Leseabstand. Bei
// Helvetica ist die Versalhoehe rund 0,717 em, ein Punkt sind 0,3528 mm —
// also Versalhoehe in mm ≈ 0,253 × Schriftgrad in pt. Umgestellt: fuer drei
// Meter braucht es 10 mm Versalhoehe und damit rund 40 pt.
//
// Ein Aushang hat drei Leseabstaende, und es waere Unfug, alles auf den
// groessten zu setzen — dann passte nichts mehr aufs Blatt:
//   Stufe 1 (aus 3 m, im Vorbeigehen): Kopfband, Projektname, Notrufnummern.
//   Stufe 2 (aus 1,5 m, vom Weg aus):  Blockueberschriften, Rufnummern,
//                                      Sammelplatz, Krankenhaus, D-Arzt.
//   Stufe 3 (davorstehend, ~1 m):      Regeln und Notfallschritte — die liest
//                                      man ohnehin nur einmal bewusst.
// Auf A2 verschieben sich alle drei Abstaende um den Faktor 1,41.

const SCHRIFT = {
    /** 40 pt → 10,1 mm Versalhoehe → 3,0 m. Stufe 1. */
    band: 40,
    /** 32 pt → 8,1 mm → 2,4 m. Stufe 1; schrumpft bei langen Namen bis 20 pt. */
    projekt: 32,
    projektMin: 20,
    /** 17 pt → 4,3 mm → 1,3 m. */
    adresse: 17,
    /** 48 pt → 12,1 mm → 3,6 m. Die wichtigste Zahl auf dem Blatt. */
    notrufNummer: 48,
    /** 14 pt → 3,5 mm → 1,1 m. Steht direkt neben der Zahl. */
    notrufWas: 14,
    /** 16 pt → 4,0 mm → 1,2 m. Stufe 2. */
    blockTitel: 16,
    /** 15 pt → 3,8 mm → 1,1 m. Stufe 2. */
    blockWert: 15,
    /** 13 pt → 3,3 mm → 1,0 m. Stufe 3. */
    regel: 13,
    /** 11 pt. Hinweistexte, die eine Luecke erklaeren. */
    hinweis: 11,
    /** 9 pt. Fussleiste — sie wird nicht gelesen, sie wird nachgeschlagen. */
    fuss: 9,
};

// ═══════════════════════════════════════════════════════════════════════════
// FESTE INHALTE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Notrufnummern.
 *
 * FEST verdrahtet und ausdruecklich nicht erfassbar: 112 und 110 gelten
 * bundesweit, und ein Eingabefeld dafuer waere ein Feld, in dem ein Tippfehler
 * moeglich ist. Wortlaut abgestimmt mit `NOTRUF_GRUNDTEXT` in
 * `services/sigeEinweisung.ts` — was am Bauzaun haengt und was in der
 * Einweisung gesagt wird, muss dasselbe sein.
 */
const NOTRUFNUMMERN: { was: string; nummer: string }[] = [
    { was: 'Rettungsdienst / Feuerwehr', nummer: '112' },
    { was: 'Polizei', nummer: '110' },
    { was: 'Giftnotruf', nummer: '030 19240' },
];

/**
 * Hoechstzahl der Regeln auf dem Aushang.
 *
 * Aus der Vorlage uebernommen (`OnePagerPage.tsx:212-215`, dort
 * `regeln.slice(0, 8)`). Acht Zeilen sind das, was jemand im Vorbeigehen
 * aufnimmt; die neunte liest niemand mehr. Wird gekappt, sagt das Blatt das
 * ausdrueckliche — eine stillschweigend abgeschnittene Baustellenordnung
 * sieht aus wie die vollstaendige.
 */
export const AUSHANG_REGELN_HOECHSTZAHL = 8;

/**
 * Der EINE Vermerk unter den Regeln.
 *
 * Es gibt zwei Gruende, warum eine Regel nicht auf dem Blatt steht: die
 * Hoechstzahl (`gesamt` > 8) und der Platz (lange Regeln laufen sonst in die
 * Fussleiste). Beide wurden frueher getrennt vermerkt — und weil ein Vermerk
 * notfalls unmittelbar ueber die Fussleiste geklemmt wird, landeten die zwei
 * Saetze auf DERSELBEN Zeile und ueberdruckten sich zu einem unlesbaren
 * Klumpen. Sie werden deshalb zu einem Satz zusammengefasst.
 *
 * Nebenbei stimmt damit auch die Zahl: „Hier stehen 8 von 12 Regeln" war
 * falsch, sobald von den acht noch welche dem Platz zum Opfer fielen.
 *
 * Leerstring heisst „nichts zu vermerken" — es steht alles auf dem Blatt.
 */
export const regelVermerk = (gesamt: number, ungedruckt = 0): string => {
    const gedruckt = Math.max(0, Math.min(gesamt, AUSHANG_REGELN_HOECHSTZAHL) - ungedruckt);
    if (gedruckt >= gesamt) return '';
    return `Hier stehen ${gedruckt} von ${gesamt} Regeln`
        + (ungedruckt > 0 ? `; ${ungedruckt} davon haben auf diesem Blatt keinen Platz mehr` : '')
        + '. Die vollständige Baustellenordnung steht im SiGe-Handbuch und wird in der Einweisung erläutert.';
};

/** Der Vermerk, wenn allein die Hoechstzahl gekappt hat — der Regelfall.
 *  Wortlaut unveraendert; `regelVermerk(gesamt, 0)` sagt dasselbe. */
export const kappungsVermerk = (gesamt: number): string =>
    `Hier stehen ${AUSHANG_REGELN_HOECHSTZAHL} von ${gesamt} Regeln. `
    + 'Die vollständige Baustellenordnung steht im SiGe-Handbuch und wird in der Einweisung erläutert.';

/** Der Satz, der die Stelle des fehlenden Notrufplans einnimmt. */
export const OHNE_NOTRUFPLAN =
    'Im SiGe-Plan ist kein Notrufplan hinterlegt. Krankenhaus und Durchgangsarzt '
    + 'sind dort einzutragen; dieser Aushang trägt hier nichts ein, was nicht geprüft wurde.';

/** Der Satz, der die Stelle der fehlenden Baustellenordnung einnimmt. */
export const OHNE_BAUSTELLENORDNUNG =
    'Für diese Baustelle ist noch keine Baustellenordnung festgelegt. '
    + 'Bis dahin gelten die Anweisungen der Bauleitung und die Unterweisung des eigenen Unternehmens.';

/** Der Satz, der die Stelle der fehlenden Notfallschritte einnimmt. */
export const OHNE_NOTFALLSCHRITTE =
    'Für diese Baustelle ist noch kein Ablauf für den Notfall festgelegt. '
    + 'Es gilt: Notruf 112, Unfallstelle sichern, Bauleitung verständigen.';

// ═══════════════════════════════════════════════════════════════════════════
// EINGABE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Was auf den Aushang kommt.
 *
 * Bewusst fertige Zeichenketten und nicht `ReportMetadata`: der Generator soll
 * ohne den Projektzweig testbar bleiben, und `src/types.ts` ist der
 * Bug-Magnet dieses Repositoriums. Die Abbildung aus den Projektstammdaten
 * macht `components/handbuch/handbuchIntegration.ts`.
 */
export interface AushangDaten {
    projektName: string;
    projektAdresse?: string;
    bauherrName?: string;
    bauherrTelefon?: string;
    bauleitungFirma?: string;
    bauleitungPerson?: string;
    bauleitungTelefon?: string;
    koordinatorFirma?: string;
    koordinatorPerson?: string;
    koordinatorTelefon?: string;
    /** `handbuecher.ordnung`. `null` heisst „noch keine Baustellenordnung". */
    ordnung?: Baustellenordnung | null;
    /** Inhalt von `sige_bloecke[typ='notrufplan']`. Wird NUR gelesen. */
    notrufplan?: NotrufplanInhalt | null;
    /** ISO-Tagesdatum des Stands. Wird zeichenweise formatiert, nie ueber `Date`. */
    standIso?: string;
    /** `handbuecher.version_label` bzw. die Fassung des SiGe-Plans. */
    fassung?: string;
    planNr?: string;
}

export interface AushangOptionen {
    /** Standard ist A3 hoch. */
    format?: AushangFormat;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten.
     *
     * Nur fuer Tests — dasselbe Muster wie `__docFactory` im Rechnungs-,
     * Einweisungs- und Koordinierungsgenerator. Bekommt die Blattmasse
     * gereicht, damit der Test dieselbe Seite baut, die der Generator meint.
     */
    __docFactory?: (masse: [number, number]) => jsPDF;
}

// ═══════════════════════════════════════════════════════════════════════════
// KLEINKRAM
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Wert, wie er auf die Bautafel darf: einzeilig und in CP1252 setzbar.
 *
 * Die Kodierung wird HIER schon geradegezogen und nicht erst in
 * `sichereTextausgabe`, weil `splitTextToSize` sonst eine andere Zeichenkette
 * ausmisst als die, die spaeter gesetzt wird. Warum das noetig ist, steht in
 * `winAnsi.ts`: EIN Zeichen ausserhalb von CP1252 kippt die GANZE Zeile in
 * UTF-16 — auf einem Blatt, das aus drei Metern lesbar sein soll, ist das
 * nicht bloss haesslich, sondern unlesbar.
 */
const text = (v?: string | null): string =>
    winAnsiSicher((v == null ? '' : String(v)).replace(/\s*\n\s*/g, ' ').replace(/\s+/g, ' ').trim());

/**
 * ISO-Tagesdatum als deutsches Datum, rein zeichenweise.
 *
 * Kein `new Date(...)`: dessen Ergebnis haengt an der Zeitzone der Laufzeit,
 * und ein Stand, der einen Tag danebenliegt, macht aus einem aktuellen Aushang
 * einen von gestern. Gleiche Ueberlegung wie in `sige-plan/zeitraum.ts` und
 * `koordinierung/ueberschneidung.ts`.
 */
const tag = (iso?: string | null): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '';

/** Eine Notrufplan-Stelle als eine Zeile — Name, Anschrift, Telefon. */
const stelleAlsZeilen = (
    stelle: { name?: string; adresse?: string; telefon?: string } | undefined,
): string[] => {
    if (!stelle) return [];
    const zeilen: string[] = [];
    const name = text(stelle.name);
    const adresse = text(stelle.adresse);
    const telefon = text(stelle.telefon);
    if (name) zeilen.push(name);
    if (adresse) zeilen.push(adresse);
    if (telefon) zeilen.push(`Tel. ${telefon}`);
    return zeilen;
};

// ═══════════════════════════════════════════════════════════════════════════
// DER SATZ
// ═══════════════════════════════════════════════════════════════════════════

const SCHWARZ: [number, number, number] = [0, 0, 0];
const ROT: [number, number, number] = [176, 20, 20];
const GRAU: [number, number, number] = [90, 90, 90];
const HELLGRAU: [number, number, number] = [240, 240, 240];

const renderAushang = (doc: jsPDF, daten: AushangDaten, format: AushangFormat): void => {
    const k = SKALA[format];
    const [blattBreite, blattHoehe] = AUSHANG_MASSE[format];

    /** A3-Millimeter in Blattmillimeter. */
    const mm = (v: number) => v * k;
    /** A3-Punkte in Blattpunkte. */
    const pt = (v: number) => v * k;

    const RAND = mm(8);
    const links = RAND;
    const rechts = blattBreite - RAND;
    const breite = rechts - links;
    const PAD = mm(6);

    // Die Fussleiste ist am Blatt festgemacht und wird zuletzt gezeichnet.
    // Ihre Oberkante steht aber SCHON JETZT fest, denn sie ist die Grenze,
    // gegen die der Satz oben abklemmt: ein Aushang hat genau eine Seite, und
    // was nicht darauf passt, darf nicht unter die Fussleiste laufen.
    const fussH = mm(11);
    const fussY = blattHoehe - RAND - fussH;
    const inhaltUnten = fussY - mm(4);

    doc.setLineJoin('miter');

    const fuellen = (farbe: [number, number, number]) => doc.setFillColor(farbe[0], farbe[1], farbe[2]);
    const schreiben = (farbe: [number, number, number]) => doc.setTextColor(farbe[0], farbe[1], farbe[2]);

    // ── Aussenrahmen ────────────────────────────────────────────────────
    // Der Rahmen ist kein Zierrat: er grenzt den Aushang gegen alles ab, was
    // sonst noch am selben Bauzaun haengt.
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(mm(1.2));
    doc.rect(links, RAND, breite, blattHoehe - 2 * RAND);

    let y = RAND;

    // ── Kopfband ────────────────────────────────────────────────────────
    const bandH = mm(24);
    fuellen(SCHWARZ);
    doc.rect(links, y, breite, bandH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(pt(SCHRIFT.band));
    schreiben([255, 255, 255]);
    doc.text('BAUSTELLENINFORMATION', links + breite / 2, y + bandH * 0.7, { align: 'center' });
    y += bandH;

    // ── Projektname und Anschrift ───────────────────────────────────────
    y += mm(9);
    const name = text(daten.projektName) || 'Baustelle';
    // Der Projektname darf nicht ueber den Rand laufen und auch nicht
    // umbrechen: zwei Zeilen Projektname draengen die Notrufnummern nach
    // unten. Er wird deshalb so weit verkleinert, bis er in eine Zeile passt —
    // und nicht weiter als bis zur Stufe-2-Groesse.
    let projektGrad = SCHRIFT.projekt;
    doc.setFont('helvetica', 'bold');
    while (projektGrad > SCHRIFT.projektMin) {
        doc.setFontSize(pt(projektGrad));
        if (doc.getTextWidth(name) <= breite - 2 * PAD) break;
        projektGrad -= 1;
    }
    doc.setFontSize(pt(projektGrad));
    schreiben(SCHWARZ);
    const nameZeilen = doc.splitTextToSize(name, breite - 2 * PAD) as string[];
    nameZeilen.forEach(zeile => {
        y += mm(projektGrad * 0.32);
        doc.text(zeile, links + breite / 2, y, { align: 'center' });
    });

    const adresse = text(daten.projektAdresse);
    if (adresse) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(pt(SCHRIFT.adresse));
        schreiben(GRAU);
        y += mm(7);
        doc.text(adresse, links + breite / 2, y, { align: 'center' });
    }
    y += mm(7);

    doc.setLineWidth(mm(0.8));
    doc.line(links, y, rechts, y);

    // ─────────────────────────────────────────────────────────────────────
    // Werkzeug fuer die Bloecke
    // ─────────────────────────────────────────────────────────────────────

    /** Ueberschriftband eines Blocks. Gibt die neue Schreibhoehe zurueck. */
    const blockTitel = (
        titel: string, x: number, oben: number, b: number, farbe: [number, number, number],
    ): number => {
        const h = mm(8);
        // Nichts wird unter die Fussleiste gesetzt — der Aushang hat genau
        // eine Seite, und ein halb ueberdrucktes Band waere schlimmer als
        // ein fehlender Block.
        if (oben + h > inhaltUnten) return oben;
        fuellen(farbe);
        doc.rect(x, oben, b, h, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(pt(SCHRIFT.blockTitel));
        schreiben([255, 255, 255]);
        doc.text(titel.toUpperCase(), x + mm(2.5), oben + h * 0.72);
        return oben + h + mm(3);
    };

    /** Wertzeilen eines Blocks. Gibt die neue Schreibhoehe zurueck. */
    const blockWerte = (
        zeilen: string[], x: number, oben: number, b: number, fett = false,
    ): number => {
        let cy = oben;
        doc.setFont('helvetica', fett ? 'bold' : 'normal');
        doc.setFontSize(pt(SCHRIFT.blockWert));
        schreiben(SCHWARZ);
        zeilen.forEach(roh => {
            (doc.splitTextToSize(roh, b) as string[]).forEach(z => {
                if (cy + mm(SCHRIFT.blockWert * 0.36) > inhaltUnten) return;
                cy += mm(SCHRIFT.blockWert * 0.36);
                doc.text(z, x, cy);
            });
        });
        return cy;
    };

    /** Hinweistext an der Stelle, an der Daten fehlen. Kursiv und grau —
     *  er soll als Erklaerung gelesen werden, nicht als Angabe. */
    const hinweisText = (satz: string, x: number, oben: number, b: number): number => {
        let cy = oben;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(pt(SCHRIFT.hinweis));
        schreiben(GRAU);
        (doc.splitTextToSize(satz, b) as string[]).forEach(z => {
            if (cy + mm(SCHRIFT.hinweis * 0.36) > inhaltUnten) return;
            cy += mm(SCHRIFT.hinweis * 0.36);
            doc.text(z, x, cy);
        });
        return cy;
    };

    /**
     * Ein Vermerk, der IN JEDEM FALL sichtbar sein muss.
     *
     * Er sagt, dass etwas ausgelassen wurde — genau deshalb darf er nicht
     * selbst dem Platzmangel zum Opfer fallen, der ihn ausgeloest hat. Er
     * wird notfalls unmittelbar ueber die Fussleiste gesetzt.
     */
    const vermerk = (satz: string, wunschHoehe: number): number =>
        hinweisText(satz, links + PAD, Math.min(wunschHoehe, inhaltUnten - mm(9)), breite - 2 * PAD);

    // ── Zwei Spalten: Verantwortliche links, Notfall rechts ─────────────
    const spaltenOben = y + mm(6);
    const spaltenBreite = (breite - mm(6)) / 2;
    const linkeX = links + PAD;
    const rechteX = links + spaltenBreite + mm(6) + PAD;
    const spalteInhalt = spaltenBreite - 2 * PAD;

    // Trennstrich zwischen den Spalten — er wird erst am Ende gezogen, wenn
    // beide Spaltenhoehen feststehen.
    let ly = spaltenOben;

    ly = blockTitel('Bauherr', linkeX - mm(2.5), ly, spalteInhalt + mm(5), SCHWARZ);
    const bauherr = [text(daten.bauherrName)].filter(Boolean);
    const bauherrTel = text(daten.bauherrTelefon);
    if (bauherr.length) ly = blockWerte(bauherr, linkeX, ly, spalteInhalt, true);
    if (bauherrTel) ly = blockWerte([`Tel. ${bauherrTel}`], linkeX, ly, spalteInhalt, true);
    if (!bauherr.length && !bauherrTel) ly = hinweisText('nicht angegeben', linkeX, ly, spalteInhalt);
    ly += mm(6);

    ly = blockTitel('Bauleitung / GU', linkeX - mm(2.5), ly, spalteInhalt + mm(5), SCHWARZ);
    const bauleitung = [text(daten.bauleitungFirma), text(daten.bauleitungPerson)].filter(Boolean);
    const bauleitungTel = text(daten.bauleitungTelefon);
    if (bauleitung.length) ly = blockWerte(bauleitung, linkeX, ly, spalteInhalt, true);
    if (bauleitungTel) ly = blockWerte([`Tel. ${bauleitungTel}`], linkeX, ly, spalteInhalt, true);
    if (!bauleitung.length && !bauleitungTel) ly = hinweisText('nicht angegeben', linkeX, ly, spalteInhalt);
    ly += mm(6);

    ly = blockTitel('SiGeKo', linkeX - mm(2.5), ly, spalteInhalt + mm(5), SCHWARZ);
    const sigeko = [text(daten.koordinatorFirma), text(daten.koordinatorPerson)].filter(Boolean);
    const sigekoTel = text(daten.koordinatorTelefon);
    if (sigeko.length) ly = blockWerte(sigeko, linkeX, ly, spalteInhalt, true);
    if (sigekoTel) ly = blockWerte([`Tel. ${sigekoTel}`], linkeX, ly, spalteInhalt, true);
    if (!sigeko.length && !sigekoTel) ly = hinweisText('nicht angegeben', linkeX, ly, spalteInhalt);

    // ── Rechte Spalte: Notfall ──────────────────────────────────────────
    let ry = spaltenOben;
    ry = blockTitel('Notruf', rechteX - mm(2.5), ry, spalteInhalt + mm(5), ROT);
    NOTRUFNUMMERN.forEach(eintrag => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(pt(SCHRIFT.notrufNummer));
        schreiben(ROT);
        ry += mm(SCHRIFT.notrufNummer * 0.30);
        doc.text(eintrag.nummer, rechteX, ry);
        // Die Beschriftung steht rechts NEBEN der Zahl: die Zahl ist die
        // Information, das Wort erklaert sie nur.
        const zahlBreite = doc.getTextWidth(eintrag.nummer);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(pt(SCHRIFT.notrufWas));
        schreiben(SCHWARZ);
        doc.text(eintrag.was, rechteX + zahlBreite + mm(4), ry - mm(1.5));
        ry += mm(2);
    });
    ry += mm(4);

    ry = blockTitel('Sammelplatz', rechteX - mm(2.5), ry, spalteInhalt + mm(5), SCHWARZ);
    const sammelplatz = text(daten.ordnung?.sammelplatz);
    ry = sammelplatz
        ? blockWerte([sammelplatz], rechteX, ry, spalteInhalt, true)
        : hinweisText('noch nicht festgelegt', rechteX, ry, spalteInhalt);
    ry += mm(6);

    // Krankenhaus und Durchgangsarzt: AUSSCHLIESSLICH aus dem Notrufplan des
    // SiGe-Plans. Fehlt er, steht hier der Hinweis und nicht etwa ein leerer
    // Kasten — ein leerer Kasten sieht aus wie „gibt es keins".
    const notruf = daten.notrufplan || null;
    const krankenhaus = stelleAlsZeilen(notruf?.krankenhaus);
    const durchgangsarzt = stelleAlsZeilen(notruf?.durchgangsarzt);

    if (!notruf || (!krankenhaus.length && !durchgangsarzt.length)) {
        ry = blockTitel('Krankenhaus / Durchgangsarzt', rechteX - mm(2.5), ry, spalteInhalt + mm(5), SCHWARZ);
        ry = hinweisText(OHNE_NOTRUFPLAN, rechteX, ry, spalteInhalt);
    } else {
        ry = blockTitel('Krankenhaus', rechteX - mm(2.5), ry, spalteInhalt + mm(5), SCHWARZ);
        ry = krankenhaus.length
            ? blockWerte(krankenhaus, rechteX, ry, spalteInhalt, true)
            : hinweisText('im Notrufplan nicht eingetragen', rechteX, ry, spalteInhalt);
        ry += mm(6);
        ry = blockTitel('Durchgangsarzt', rechteX - mm(2.5), ry, spalteInhalt + mm(5), SCHWARZ);
        ry = durchgangsarzt.length
            ? blockWerte(durchgangsarzt, rechteX, ry, spalteInhalt, true)
            : hinweisText('im Notrufplan nicht eingetragen', rechteX, ry, spalteInhalt);
    }

    const spaltenUnten = Math.max(ly, ry) + mm(8);
    doc.setLineWidth(mm(0.5));
    doc.line(links + spaltenBreite + mm(3), spaltenOben - mm(2), links + spaltenBreite + mm(3), spaltenUnten - mm(4));
    doc.setLineWidth(mm(0.8));
    doc.line(links, spaltenUnten, rechts, spaltenUnten);

    y = spaltenUnten + mm(6);

    // ── Baustellenordnung ───────────────────────────────────────────────
    const regelnRoh = (daten.ordnung?.regeln || []).map(text).filter(Boolean);
    y = blockTitel('Baustellenordnung', links + PAD - mm(2.5), y, breite - 2 * PAD + mm(5), [51, 51, 51]);

    if (!regelnRoh.length) {
        y = hinweisText(OHNE_BAUSTELLENORDNUNG, links + PAD, y, breite - 2 * PAD);
    } else {
        const regeln = regelnRoh.slice(0, AUSHANG_REGELN_HOECHSTZAHL);
        // Zweispaltig, sobald mehr als vier Regeln dastehen — wie in der
        // Vorlage. Einspaltig waeren acht Zeilen ein Textblock, den niemand
        // im Vorbeigehen liest.
        const zweiSpalten = regeln.length > 4;
        const rBreite = zweiSpalten ? (breite - 2 * PAD - mm(10)) / 2 : breite - 2 * PAD;
        const proSpalte = zweiSpalten ? Math.ceil(regeln.length / 2) : regeln.length;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(pt(SCHRIFT.regel));
        schreiben(SCHWARZ);
        // Jede Spalte fuehrt ihre eigene Schreibhoehe: eine Regel darf
        // umbrechen, und dann rutschen nur die Regeln DARUNTER nach, nicht
        // die der Nachbarspalte.
        const spaltenHoehe = [y, y];
        let ungedruckt = 0;
        regeln.forEach((regel, i) => {
            const spalte = zweiSpalten && i >= proSpalte ? 1 : 0;
            const x = links + PAD + spalte * (rBreite + mm(10));
            const zeilen = doc.splitTextToSize(regel, rBreite - mm(6)) as string[];
            let cy = spaltenHoehe[spalte];
            // Ein Blatt, eine Seite: was nicht mehr ueber die Fussleiste
            // passt, wird nicht gedruckt und darunter vermerkt.
            if (cy + zeilen.length * mm(SCHRIFT.regel * 0.36) > inhaltUnten - mm(20)) {
                ungedruckt += 1;
                return;
            }
            doc.text('•', x, cy + mm(SCHRIFT.regel * 0.36));
            zeilen.forEach(z => {
                cy += mm(SCHRIFT.regel * 0.36);
                doc.text(z, x + mm(6), cy);
            });
            spaltenHoehe[spalte] = cy + mm(2.5);
        });
        y = Math.max(spaltenHoehe[0], spaltenHoehe[1]);
        // GENAU EIN Vermerk. Zwei aufeinanderfolgende Aufrufe klemmten beide
        // auf `inhaltUnten - mm(9)` und ueberdruckten sich (Begruendung bei
        // `regelVermerk`).
        const satz = regelVermerk(regelnRoh.length, ungedruckt);
        if (satz) y = vermerk(satz, y + mm(2));
    }

    y += mm(7);

    // ── Verhalten im Notfall ────────────────────────────────────────────
    const schritte = (daten.ordnung?.notfallschritte || []).map(text).filter(Boolean);
    y = blockTitel('Verhalten im Notfall', links + PAD - mm(2.5), y, breite - 2 * PAD + mm(5), ROT);
    if (!schritte.length) {
        y = hinweisText(OHNE_NOTFALLSCHRITTE, links + PAD, y, breite - 2 * PAD);
    } else {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(pt(SCHRIFT.regel));
        schreiben(SCHWARZ);
        let ausgelassen = 0;
        schritte.forEach((schritt, i) => {
            const zeilen = doc.splitTextToSize(schritt, breite - 2 * PAD - mm(10)) as string[];
            if (y + zeilen.length * mm(SCHRIFT.regel * 0.36) > inhaltUnten) {
                ausgelassen += 1;
                return;
            }
            doc.setFont('helvetica', 'bold');
            doc.text(`${i + 1}.`, links + PAD, y + mm(SCHRIFT.regel * 0.36));
            doc.setFont('helvetica', 'normal');
            zeilen.forEach(z => {
                y += mm(SCHRIFT.regel * 0.36);
                doc.text(z, links + PAD + mm(10), y);
            });
            y += mm(1.8);
        });
        if (ausgelassen > 0) {
            vermerk(
                `${ausgelassen} weitere Schritt(e) haben auf diesem Blatt keinen Platz mehr; `
                + 'siehe Baustellenordnung im SiGe-Handbuch.',
                y + mm(2),
            );
        }
    }

    // ── Fussleiste ──────────────────────────────────────────────────────
    fuellen(HELLGRAU);
    doc.rect(links, fussY, breite, fussH, 'F');
    doc.setLineWidth(mm(0.5));
    doc.line(links, fussY, rechts, fussY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(pt(SCHRIFT.fuss));
    schreiben(GRAU);

    const linkeFuss = [
        text(daten.planNr) && `SiGe-Plan ${text(daten.planNr)}`,
        text(daten.fassung) && `Fassung ${text(daten.fassung)}`,
    ].filter(Boolean).join(' · ');
    if (linkeFuss) doc.text(linkeFuss, links + mm(3), fussY + fussH * 0.62);
    const standText = tag(daten.standIso);
    if (standText) doc.text(`Stand: ${standText}`, rechts - mm(3), fussY + fussH * 0.62, { align: 'right' });
};

/**
 * Der Aushang als jsPDF-Dokument. Immer genau eine Seite.
 *
 * Gibt bewusst das Dokument zurueck und keine Bytes: der Aufrufer entscheidet,
 * ob er speichert, druckt oder in die Vorschau haengt.
 */
export const generateAushangPdf = (
    daten: AushangDaten,
    optionen?: AushangOptionen,
): jsPDF => {
    const format: AushangFormat = optionen?.format === 'a2' ? 'a2' : 'a3';
    const masse = AUSHANG_MASSE[format];
    // `orientation: 'portrait'` UND ein Hochformat-Array: jsPDF dreht das
    // Format notfalls passend zur Ausrichtung, und beides zusammen laesst
    // keinen Zweifel, welche Kante oben ist. Muster: generatorSigePlan.ts:125.
    const doc = optionen?.__docFactory
        ? optionen.__docFactory(masse)
        : new jsPDF({ unit: 'mm', orientation: 'portrait', format: [...masse], compress: true });
    // Letzte Rueckfallebene fuer alles, was nicht durch `text()` gelaufen ist.
    sichereTextausgabe(doc);
    renderAushang(doc, daten || ({ projektName: '' } as AushangDaten), format);
    return doc;
};

/**
 * Dateiname des Aushangs.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage-Bucket, und dort ist ein Schraegstrich aus einem
 * Projektnamen ein neuer Ordner.
 */
export const aushangDateiname = (
    projektName: string,
    format: AushangFormat = 'a3',
    datumIso?: string,
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['Aushang', sauber(projektName), format.toUpperCase()];
    teile.push((datumIso || '').slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};
